'use client';

import React from 'react';
import Link from 'next/link';

import { Button, SvgIconWrapper, Typography } from 'lighthouse';
import { ChevronLeftOffOutlineMdSVG } from 'lighthouse-icons';

import AddToCartButton from '@/widgets/AddToCartButton';
import { SafeImage } from '@/widgets/SafeImage';
import { StoreCityBadge } from '@/widgets/StoreCityBadge';

import { rialToToman } from '@/utilities/Financial';
import { getFulfillmentLabel, isZeroLaborFeeFulfillment } from '@/utilities/fulfillment';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { withSeparator } from '@/utilities/withSeparator';

import { CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { productRequestRouteByVehicleType } from '@/constants/product';

import PricingDetail from '../PricingDetails';
import { SellerRatingChips } from '../SellerRatingChips';
import { BuyBoxSkeleton } from './BuyBoxSkeleton';
import { BuyBoxProps } from './models';
import { TrustBadges } from './TrustBadges';
import { UnavailableBuyBox } from './UnavailableBuyBox';

export const BuyBox = ({
    winner,
    allStores,
    available,
    isPriceLoading,
    category,
    guarantee,
    vehicleType,
    uuid,
    onViewAllVendors,
    selectedFulfillmentProfile
}: BuyBoxProps) => {
    const otherVendorsCount = Math.max(allStores.length - 1, 0);

    if (isPriceLoading) {
        return <BuyBoxSkeleton />;
    }

    if (!available) {
        return <UnavailableBuyBox isOutOfCoverage={winner.availableQuantity > 0} />;
    }

    const selectedLaborFee = isZeroLaborFeeFulfillment(selectedFulfillmentProfile?.fulfillmentType) ? 0 : selectedFulfillmentProfile?.laborFee || 0;
    const totalPriceWithFulfillment = (winner.sellingPrice || winner.price) + selectedLaborFee;
    const showLaborFee = selectedFulfillmentProfile?.laborFee != null && !isZeroLaborFeeFulfillment(selectedFulfillmentProfile.fulfillmentType);

    return (
        <aside
            className='nice-scroll sticky top-20 max-h-[calc(100vh-6rem)] w-full self-start overflow-y-auto rounded-lg bg-white
                shadow-[0_1px_3px_rgba(15,60,100,0.05),0_10px_30px_rgba(15,60,100,0.07)]'>
            <div className='flex flex-col gap-4 p-4'>
                <div className='flex items-center justify-between'>
                    <Typography className='text-sm font-bold text-blackHighEmphasis'>فروشنده</Typography>
                    {otherVendorsCount > 0 && (
                        <Button variant='text' size='xSmall' onClick={onViewAllVendors} className='!min-w-0 !p-0 text-xs font-semibold'>
                            {otherVendorsCount} فروشنده دیگر
                        </Button>
                    )}
                </div>

                <Link target='_blank' href={`/vendor/${winner.storeUuid}`} className='flex items-center gap-3'>
                    <figure className='m-0 h-12 w-12 shrink-0 rounded-lg'>
                        <SafeImage
                            src={
                                GenerateFilePreviewUrl({
                                    attachableId: winner.storeUuid,
                                    attachment: 'STORE',
                                    type: 'LOGO',
                                    isPrimary: true,
                                    sortOrder: 0
                                }).src
                            }
                            alt={winner.storeName}
                            fallback='/images/vendor/vendor_profile_default.png'
                            className='h-12 w-12 overflow-hidden rounded-lg'
                            width={48}
                            height={48}
                        />
                    </figure>
                    <div className='min-w-0 flex-1'>
                        <div className='flex min-w-0 items-center gap-2'>
                            <span className='min-w-0 truncate text-sm font-semibold text-blackHighEmphasis'>{winner.storeName}</span>
                            {winner.storeCityName?.trim() && (
                                <span className='flex max-w-[45%] shrink-0 items-center border-0 border-s border-solid border-gray-200 ps-2'>
                                    <StoreCityBadge cityName={winner.storeCityName} compact />
                                </span>
                            )}
                        </div>
                        <div className='mt-1 flex items-center gap-2'>
                            <SellerRatingChips storeRating={winner.storeRating} storeReviewCount={winner.storeReviewCount} size='sm' />
                        </div>
                    </div>
                    <SvgIconWrapper icon={ChevronLeftOffOutlineMdSVG} size='md' color='blackMediumEmphasis' className='shrink-0' />
                </Link>

                <hr className='-my-1 w-full opacity-10' />

                {!!guarantee && (
                    <div className='flex items-center justify-between text-sm'>
                        <Typography variant='body2' color='blackMediumEmphasis'>
                            گارانتی
                        </Typography>
                        <Typography variant='body2' color='blackHighEmphasis' className='font-semibold'>
                            {guarantee} ماه
                        </Typography>
                    </div>
                )}

                <div className='rounded-lg bg-gray-50 p-3'>
                    <div className='flex items-center justify-between'>
                        <Typography variant='body2' color='blackMediumEmphasis'>
                            قیمت
                        </Typography>
                        <PricingDetail
                            available={available}
                            isPriceLoading={isPriceLoading}
                            discount={winner.discountPercentage}
                            sellingPrice={winner.sellingPrice}
                            price={winner.price}
                        />
                    </div>

                    {!!selectedFulfillmentProfile && (
                        <>
                            <hr className='my-3 w-full opacity-10' />
                            <div className='space-y-2'>
                                <div className='flex items-center justify-between'>
                                    <Typography variant='caption' color='blackMediumEmphasis'>
                                        روش تحویل
                                    </Typography>
                                    <Typography variant='body2' fontWeight={600} color='blackHighEmphasis'>
                                        {getFulfillmentLabel(selectedFulfillmentProfile.fulfillmentType)}
                                    </Typography>
                                </div>

                                {showLaborFee ? (
                                    <div className='flex items-center justify-between'>
                                        <Typography variant='caption' color='blackMediumEmphasis'>
                                            اجرت تعویض
                                        </Typography>
                                        <Typography variant='body2' color={selectedLaborFee === 0 ? 'success' : 'blackHighEmphasis'}>
                                            {selectedLaborFee === 0 ? 'رایگان' : `${withSeparator(rialToToman(selectedLaborFee))} تومان`}
                                        </Typography>
                                    </div>
                                ) : null}
                                <div className='flex items-center justify-between'>
                                    <Typography variant='body2' color='blackMediumEmphasis'>
                                        جمع قابل پرداخت
                                    </Typography>
                                    <Typography variant='subtitle2' color='primary' fontWeight={700}>
                                        {withSeparator(rialToToman(totalPriceWithFulfillment))} <span className='text-xs font-normal'>تومان</span>
                                    </Typography>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <AddToCartButton
                    size='large'
                    isMainVendor={category.slug === CAR_BATTERY_VEHICLE_TYPE_ID || category.slug === MOTOR_BATTERY_VEHICLE_TYPE_ID}
                    available={available}
                    allStores={allStores}
                    winner={winner}
                    href={available ? `/${vehicleType}${productRequestRouteByVehicleType[vehicleType]}?productId=${uuid}` : ''}
                    isPriceLoading={isPriceLoading || false}
                    selectedFulfillmentProfile={selectedFulfillmentProfile}
                />

                <TrustBadges />
            </div>
        </aside>
    );
};
